import React from 'react';
import AdvocacyCard from '../AdvocacyCard';
import SponsoredTeaser from '../SponsoredTeaser';
import Advertisement from 'components/01-composites/advertisement';
import { format } from 'date-fns';

function getDAMUrl(vid, name){
  const id = vid || '';
  return `https://americascus.widen.net/content/${id}/web/${name}`;
}

/**
 * Format an advertisment card.
 * @param {*} card
 * @returns {Object} vars
 */
function preprocessAdCard(card) {
  const vars = {
    src: null,
    alt: card.label,
    linkAttributes: null,
  }
  if (card.fieldLink) {
    vars.linkAttributes = {
      href: card.fieldLink.uri.path,
      title: card.label,
    };
    if ('attributes' in card.fieldLink?.options) {
      vars.linkAttributes = {...vars.linkAttributes, ...card.fieldLink.options.attributes};
      if ('class' in vars.linkAttributes) {
        // Convert the class array to a string.
        vars.linkAttributes.class = vars.linkAttributes.class.join(' ');
      }
    }
  }
  if (card.fieldImage) {
    if ('acquiaDamAssetId' in card.fieldImage) {
      const { name, acquiaDamAssetId } = card.fieldImage;
      vars.src = getDAMUrl(acquiaDamAssetId.versionId, name);
    } else if ('thumbnail' in card.fieldImage) {
      vars.src = card.fieldImage.thumbnail.image.urlPath;
    }
  }
  return vars;
}

/**
 * Formats data from graphql to be used in
 * Advocacy comm, blogs, issues, and news cards.
 *
 * @param {Object} card
 * @returns {Object} vars
 */
function preprocessCard(card) {
  const vars = {};
  // Show gated icon if the content is gated.
  vars.gated = card?.fieldMemberOnlyContent || false;
  // Set link if the content is not gated.
  if ('url' in card) {
    vars.link = card.url.path;
  }
  // Check for Asset.
  if ('fieldAsset' in card && 'acquiaDamAssetId' in card.fieldAsset) {
    const { name, acquiaDamAssetId } = card.fieldAsset;
    // Advocacy communication cards have a link to work with content gating.
    if (card.entityBundle === 'advocacy_communication') {
      vars.link = `/advocacy/${card.id}/asset/download`;
    } else {
      vars.link = getDAMUrl(acquiaDamAssetId?.versionId, name);
    }
  }
  // Eyebrow.
  if (card.fieldPublishDate) {
    const publishDate = new Date(card.fieldPublishDate.formatted).valueOf();
    // Make sure we convert back to Central Time (-06:00).
    const offset = 3600000 * 6;
    vars.eyebrow = format(new Date(publishDate + offset), 'MMMM d, yyyy');
  } else if (card.entityBundle === 'issue' && card.fieldAdvocacyIssueTypes.length > 0) {
    // Get the first issue type.
    vars.eyebrow = card.fieldAdvocacyIssueTypes[0].label;
  }
  // Get the tag for the card.
  if ('type' in card) {
    vars.tag = card.type?.label;
  } else if ('fieldCouncilResource' in card) {
    vars.tag = card.fieldCouncilResource?.label;
  }
  // Recipient/Agency at the bottom of the card.
  vars.body = card?.fieldRecipientAgency?.label;
  // If teaser description is available, use it as the body instead.
  if ('fieldTeaserDescription' in card) {
    vars.body = card.fieldTeaserDescription;
  }
  // Check for teaser iamge.
  if (card.fieldBanner) {
    vars.teaserImg = {};
    if ('acquiaDamAssetId' in card.fieldBanner) {
      const { name, acquiaDamAssetId } = card.fieldBanner;
      vars.teaserImg.urlPath = getDAMUrl(acquiaDamAssetId?.versionId, name) + '?width=400';
      vars.teaserImg.alt = name;
    } else if ('thumbnail' in card.fieldBanner) {
      const { image } = card.fieldBanner.thumbnail;
      vars.teaserImg.urlPath = image.urlPath;
      vars.teaserImg.alt = card.label;
    }
  }
  // Check for Councils Resource categories.
  if (card.fieldCouncilAreaCategorizatio && card.fieldCouncilAreaCategorizatio.length > 0) {
    vars.categories = card.fieldCouncilAreaCategorizatio;
  }

  return vars;
}

/**
 * Renders a list of Advocacy Cards.
 * @param {Array} result
 * @returns {Array}
 */
function renderResults(result) {
  if (result.length === 0) {
    return <h2> No results found </h2>;
  }
  return result.map((item) => {
    switch(item.entityBundle) {
      case 'advertisement':
        const props = preprocessAdCard(item);
        props.key = item.id;
        return (
          <Advertisement { ...props } />
        );

      case 'news':
        const { fieldSponsored } = item;
        if (fieldSponsored) {
          const { gated, link, tag, body, eyebrow, teaserImg } = preprocessCard(item);
          return <SponsoredTeaser
            key={item.id}
            gated={gated}
            link={link}
            tag={tag}
            title={item.label}
            body={body}
            media={teaserImg}
            eyebrow={eyebrow}
          />
        }


      default:
        const { gated, link, tag, eyebrow, body, teaserImg, categories } = preprocessCard(item);
        return (
          <AdvocacyCard
            containerClass={tag ? "card-" + tag.toLowerCase() : "card"}
            key={item.id}
            gated={gated}
            link={link}
            tag={tag}
            categories={categories}
            eyebrow={eyebrow}
            title={item.label}
            body={body}
            media={teaserImg}
            bundle={item.entityBundle}
          />
        );
    }
  });
}

export default renderResults;
